/**
 * The Files tab — §7 of the record page plan.
 *
 * One tab, every record. What is different here from the attachments card it
 * replaces is that a file is *linked* rather than owned, so the same tab shows
 * two kinds of row and says which is which:
 *
 *   **Filed here** — somebody put it on this record. It can be retyped,
 *     re-described, and taken off again.
 *   **From jobs and visits** — a close-out photo, a signed quote. It is shown
 *     with where it came from, and the ⋯ menu offers to open that place
 *     instead of editing it, because this is not where it lives. Editing a
 *     crew's photo from the property it happens to appear on is how two people
 *     end up believing different things about the same file.
 *
 * Grid or rows is a per-person preference, remembered per device — a Photos
 * tab wants thumbnails and a Contracts tab wants a list, and the same person
 * wants different things on Monday morning than on a phone in a truck.
 *
 * The whole tab is a drop target. Not a 200px dashed box inside the tab: the
 * tab. Somebody dragging a PDF out of Mail is aiming at the page, and a drop
 * zone you have to find is a drop zone that loses files.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react'
import {
  FILE_TYPES, FILE_TYPE_LABELS, FILE_VISIBILITIES, FILE_VISIBILITY_LABELS,
  type FileType, type FileVisibility, type RecordType,
} from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { request } from '../lib/api.js'
import { usePhone } from '../lib/use-narrow.js'
import {
  Button, Chip, EmptyState, Segmented, Tag,
} from '../components/primitives.js'
import { ContextMenu, type MenuItem } from '../components/context-menu.js'
import { Modal } from '../components/overlays.js'
import { DateField } from '../components/date-field.js'
import { MenuSelect } from '../components/menu-select.js'
import { RecordCard } from './record-rail.js'
import { useRecordUpload } from './use-record-upload.js'
import {
  DEFAULT_FILE_TYPES, FilesEmpty, clipboardFiles, clipboardReadable,
} from './record-empties.js'
import {
  IconExternal, IconFile, IconGrid, IconImage, IconRows, IconUpload, IconMoreVertical,
} from '../frame/icons.js'
import { formatDateOnly, formatRelative } from '../lib/format.js'

export interface RecordFile {
  id: string
  name: string
  caption: string
  contentType: string
  byteSize: number
  type: FileType
  visibility: FileVisibility
  expiresOn: string | null
  expiresInDays: number | null
  uploadedBy: string | null
  uploadedAt: string
  url: string
  source: { label: string; href: string } | null
}

/**
 * The filter chips above the files.
 *
 * Not one chip per type. "Permits & insurance" is one chip because nobody has
 * ever gone looking for a permit and not also been happy to see the
 * certificate of insurance beside it, and eight chips wrap onto two rows.
 */
const FILTERS: { key: string; label: string; types?: FileType[]; rolled?: boolean }[] = [
  { key: 'all', label: 'All' },
  { key: 'contracts', label: 'Contracts', types: ['contract'] },
  { key: 'plans', label: 'Site plans', types: ['site_plan'] },
  { key: 'photos', label: 'Photos', types: ['photo'] },
  { key: 'compliance', label: 'Permits & insurance', types: ['permit', 'insurance'] },
  { key: 'rolled', label: 'From jobs & visits', rolled: true },
]

const LAYOUT_KEY = 'record.files.layout'

export function RecordFilesTab({
  object, id, orgUnitId, canEdit = true, fileTypes, onChanged, onReady,
}: {
  object: RecordType
  id: string
  /**
   * Which type chips the empty state offers — `RecordConfig.fileTypes`.
   *
   * §11's fourth decision, with its default taken: the same five everywhere
   * unless a record says otherwise, which is why this is optional rather
   * than a table of per-record lists nobody has asked for yet.
   */
  fileTypes?: FileType[]
  /** Which branch the uploaded file is filed under. */
  orgUnitId: string
  canEdit?: boolean
  /** Called after anything is added, retyped or taken off — the tab count. */
  onChanged?: () => void
  /**
   * Hands the page a way to open the file picker.
   *
   * On a phone the green quick action under the title *is* the Upload button
   * (§8), and it is drawn by the shell, not by this tab — so the tab lends it
   * the one thing only the tab has, which is the `<input type="file">`. Two
   * Upload buttons one above the other was the alternative, and it is the
   * sort of thing nobody reports and everybody notices.
   */
  onReady?: (openPicker: () => void) => void
}) {
  const phone = usePhone()
  const [files, setFiles] = useState<RecordFile[] | null>(null)
  const [filter, setFilter] = useState('all')
  const [removing, setRemoving] = useState(false)
  /* Reading the list is this tab's own, so its failure is its own too. */
  const [loadError, setLoadError] = useState<string | null>(null)

  /*
   * **Uploading is `useRecordUpload`, not this tab** (`quiet-records.md` §4).
   *
   * It owns the hidden input, the drag counter, the per-file try, the
   * progress and the describe dialog, because the quiet Overview makes the
   * whole page a drop target and two copies of that would drift. The tab
   * keeps what is genuinely its own: the list, the filter chips, the layout
   * toggle and taking a file off.
   */
  const uploader = useRecordUpload({
    object,
    id,
    orgUnitId,
    canEdit,
    onChanged,
    onFiles: setFiles,
  })
  const { busy: uploading, over, error: uploadError, describing, setDescribing } = uploader
  const busy = uploading || removing

  /*
   * A phone has no room for a grid of thumbnails *and* the three lines under
   * each one, so it is always rows there (§7.2). The preference is still
   * remembered — it is the desktop's, and a phone borrowing it would be a
   * setting somebody changed on a laptop breaking a screen they never opened.
   */
  const [layout, setLayout] = useState<'grid' | 'rows'>(
    () => (localStorage.getItem(LAYOUT_KEY) === 'grid' ? 'grid' : 'rows'))
  const shown = phone ? 'rows' : layout

  const load = useCallback(async () => {
    try {
      const body = await request<{ items: RecordFile[] }>(`/records/${object}/${id}/files`)
      setFiles(body.items)
      setLoadError(null)
    } catch (cause) {
      setLoadError(cause instanceof Error ? cause.message : 'Those could not be loaded.')
    }
  }, [object, id])

  useEffect(() => { void load() }, [load])

  useEffect(() => { onReady?.(uploader.openPicker) }, [onReady, uploader.openPicker])

  async function save(file: RecordFile, changes: Record<string, unknown>) {
    await request(`/records/${object}/${id}/files/${file.id}`, { method: 'PATCH', body: changes })
    await load()
    onChanged?.()
  }

  /*
   * "Remove from here", never "delete" (rule 2). The file stays; the *link*
   * to this record is what goes, and the wording says so — a button labelled
   * Delete that leaves the file on three other records is a lie, and one
   * labelled Delete that did not would break the rule.
   */
  async function unlink(file: RecordFile) {
    setRemoving(true)
    try {
      await request(`/records/${object}/${id}/files/${file.id}`, { method: 'DELETE' })
      await load()
      onChanged?.()
    } catch (cause) {
      /* Taking a file off is this tab's own job, so its failure is reported
         through the hook's one error line rather than a second one. */
      uploader.setError(cause instanceof Error ? cause.message : 'That could not be taken off.')
    } finally {
      setRemoving(false)
    }
  }

  const active = FILTERS.find((option) => option.key === filter) ?? FILTERS[0]!
  const all = files ?? []
  const visible = all.filter((file) => {
    if (active.rolled) return file.source !== null
    if (!active.types) return true
    return active.types.includes(file.type)
  })

  const countOf = (option: typeof FILTERS[number]) => (
    option.rolled
      ? all.filter((file) => file.source !== null).length
      : option.types
        ? all.filter((file) => option.types!.includes(file.type)).length
        : all.length
  )

  return (
    <div
      {...uploader.dropProps}
      className={cn('flex min-h-0 flex-1 flex-col gap-3',
        over && 'rounded-record outline-dashed outline-2 outline-offset-4 outline-accent-line')}
    >
      <input {...uploader.inputProps} />

      {/* Chips left, layout and Upload right — one row, scrolling on a phone. */}
      <div className="flex items-center gap-2">
        <div className="no-scrollbar flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto">
          {FILTERS.map((option) => {
            const count = countOf(option)
            if (count === 0 && option.key !== 'all') return null
            return (
              <Chip
                key={option.key}
                active={option.key === filter}
                onClick={() => setFilter(option.key)}
                className="shrink-0"
              >
                {option.label}
                <span className="tnum ml-1.5 opacity-70">{count}</span>
              </Chip>
            )
          })}
        </div>

        {!phone && (
          <Segmented
            label="How files are shown"
            value={layout}
            onChange={(next) => { setLayout(next); localStorage.setItem(LAYOUT_KEY, next) }}
            options={[
              { value: 'rows', label: '', glyph: <IconRows size={15} /> },
              { value: 'grid', label: '', glyph: <IconGrid size={15} /> },
            ]}
          />
        )}

        {canEdit && !phone && (
          <Button
            variant="primary"
            disabled={busy}
            onClick={() => uploader.openPicker()}
            className="shrink-0 gap-1.5"
          >
            <IconUpload size={14} />
            {busy ? 'Uploading…' : 'Upload'}
          </Button>
        )}

        {/* The phone's Upload is the green quick action; this only says so. */}
        {canEdit && phone && busy && (
          <span className="shrink-0 text-chip font-semibold text-accent">Uploading…</span>
        )}
      </div>

      {(loadError ?? uploadError) && (
        <p className="m-0 rounded-field bg-danger-bg px-3 py-2 text-body text-danger">
          {loadError ?? uploadError}
        </p>
      )}

      {/*
        * The dashed hint, and only while there is something under it. With
        * no files the empty card below *is* the drop target, at full size
        * and with the picker in it — two dashed boxes saying the same thing
        * one above the other is how a tab stops being read.
        */}
      {canEdit && !phone && files !== null && all.length > 0 && (
        <p className="m-0 rounded-record border border-dashed border-line-strong px-3 py-2.5 text-center text-body text-text-faint">
          Drop files anywhere on this tab · photos, PDFs, plans
        </p>
      )}

      {files === null
        ? <FilesSkeleton />
        : visible.length === 0
          ? filter === 'all'
            ? (
              <FilesEmpty
                phone={phone}
                canEdit={canEdit}
                types={fileTypes ?? DEFAULT_FILE_TYPES}
                onPick={(type?: FileType) => uploader.openPicker(type ? { type } : undefined)}
                {...(clipboardReadable()
                  ? { onPaste: () => void clipboardFiles((picked) => uploader.add(picked)) }
                  : {})}
              />
            )
            : (
              /* A chip with nothing behind it is not the tab being empty —
                 it is this filter being empty, and the way out is another
                 chip rather than an upload. */
              <EmptyState
                title="Nothing of that kind here"
                body="Try another chip, or upload one."
                action={canEdit ? { label: 'Upload', onClick: () => uploader.openPicker() } : undefined}
              />
            )
          : shown === 'grid'
            ? (
              <div className="grid grid-cols-[repeat(auto-fill,minmax(212px,1fr))] gap-3">
                {visible.map((file) => (
                  <FileTile
                    key={file.id}
                    file={file}
                    menu={menuFor(file, { canEdit, onDescribe: (file: RecordFile) => setDescribing([file]), onUnlink: unlink })}
                  />
                ))}
              </div>
            )
            : (
              <RecordCard title="Files" count={visible.length}>
                {visible.map((file) => (
                  <FileRow
                    key={file.id}
                    file={file}
                    menu={menuFor(file, { canEdit, onDescribe: (file: RecordFile) => setDescribing([file]), onUnlink: unlink })}
                  />
                ))}
              </RecordCard>
            )}

      {/*
        * The legend is for the chips on files that exist. The empty state
        * carries §8's own sentence about who sees a file, so drawing both
        * puts two explanations of Internal/Crew/Portal one under the other
        * on the one screen with no files to apply them to.
        */}
      {!(files !== null && all.length === 0) && <VisibilityKey />}

      {describing && describing.length > 0 && (
        <DescribeFile
          files={describing}
          onClose={() => setDescribing(null)}
          onSave={async (changes) => {
            for (const file of describing) await save(file, changes)
            setDescribing(null)
          }}
        />
      )}
    </div>
  )
}

/**
 * What the ⋯ offers, which depends on where the file lives.
 *
 * A rolled-up file gets Open and "Open the visit" and nothing else. There is
 * no Remove, because there is no link here to remove — it is on the visit —
 * and an item that failed with "that file is not on this record" would be a
 * menu item that exists only to be wrong.
 */
function menuFor(
  file: RecordFile,
  handlers: {
    canEdit: boolean
    onDescribe: (file: RecordFile) => void
    onUnlink: (file: RecordFile) => void
  },
): MenuItem[] {
  const items: MenuItem[] = [
    { key: 'open', label: 'Open', onSelect: () => window.open(file.url, '_blank', 'noopener') },
  ]

  if (file.source) {
    items.push({
      key: 'source',
      label: `Open ${file.source.label}`,
      onSelect: () => { window.location.href = file.source!.href },
    })
    return items
  }

  if (handlers.canEdit) {
    items.push({ key: 'describe', label: 'Rename and describe…', onSelect: () => handlers.onDescribe(file) })
    items.push({
      key: 'unlink',
      label: 'Remove from here',
      tone: 'danger',
      separatorBefore: true,
      onSelect: () => handlers.onUnlink(file),
    })
  }
  return items
}

// ---------------------------------------------------------------------------
// The two ways a file is drawn
// ---------------------------------------------------------------------------

function FileTile({ file, menu }: { file: RecordFile; menu: MenuItem[] }) {
  return (
    <article className="overflow-hidden rounded-record border border-line bg-surface">
      <a href={file.url} target="_blank" rel="noopener noreferrer" className="block">
        <Thumb file={file} className="h-[124px] w-full" />
      </a>
      <div className="flex items-start gap-1 px-3 pb-3 pt-2.5">
        <div className="min-w-0 flex-1">
          <a
            href={file.url}
            target="_blank"
            rel="noopener noreferrer"
            className="block truncate text-body font-semibold text-text hover:text-accent"
          >
            {file.name}
          </a>
          <FileTags file={file} />
          <FileLine file={file} />
        </div>
        <FileMenuButton menu={menu} name={file.name} />
      </div>
    </article>
  )
}

function FileRow({ file, menu }: { file: RecordFile; menu: MenuItem[] }) {
  return (
    <div className="flex items-center gap-3 border-t border-line-soft px-[18px] py-2.5">
      <a href={file.url} target="_blank" rel="noopener noreferrer" className="shrink-0">
        <Thumb file={file} className="h-11 w-11 rounded-field" />
      </a>
      <div className="min-w-0 flex-1">
        <a
          href={file.url}
          target="_blank"
          rel="noopener noreferrer"
          className="block truncate text-body font-semibold text-text hover:text-accent"
        >
          {file.name}
        </a>
        <FileTags file={file} />
        <FileLine file={file} />
      </div>
      <FileMenuButton menu={menu} name={file.name} />
    </div>
  )
}

/**
 * The thumbnail, or the glyph that stands in for one.
 *
 * An image gets its own bytes; everything else gets a document glyph on the
 * quiet panel colour. No generated preview of a PDF's first page: a thumbnail
 * of a contract is a grey rectangle with unreadable text on it, which is the
 * same amount of information as the glyph and four times the bytes.
 */
function Thumb({ file, className }: { file: RecordFile; className?: string }) {
  const isImage = file.contentType.startsWith('image/')
  return isImage
    ? (
      <img
        src={file.url}
        alt=""
        loading="lazy"
        className={cn('object-cover', className)}
      />
    )
    : (
      <span className={cn('flex items-center justify-center bg-surface-alt text-line-strong', className)}>
        {file.type === 'photo' ? <IconImage size={22} /> : <IconFile size={22} />}
      </span>
    )
}

function FileTags({ file }: { file: RecordFile }) {
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1">
      <span
        className="inline-flex h-[18px] items-center whitespace-nowrap rounded-tag px-1.5 text-tag font-semibold"
        style={{
          color: `rgb(var(--status-file-${file.type}-fg))`,
          background: `rgb(var(--status-file-${file.type}-bg))`,
        }}
      >
        {FILE_TYPE_LABELS[file.type]}
      </span>
      <Tag tone="plain">{FILE_VISIBILITY_LABELS[file.visibility]}</Tag>
      {file.expiresInDays !== null && (
        <Tag tone={file.expiresInDays <= 0 ? 'bad' : 'warn'}>
          {file.expiresInDays <= 0
            ? 'Expired'
            : `Expires in ${file.expiresInDays} ${file.expiresInDays === 1 ? 'day' : 'days'}`}
        </Tag>
      )}
    </div>
  )
}

/**
 * The 11px line underneath: where it came from, who put it there, and when.
 *
 * A rolled-up file leads with its source, because that is the thing somebody
 * looking at an unfamiliar photo on a property most needs to know.
 */
function FileLine({ file }: { file: RecordFile }) {
  const parts: ReactNode[] = []
  if (file.source) parts.push(file.source.label)
  if (file.caption) parts.push(file.caption)
  if (file.uploadedBy) parts.push(file.uploadedBy)
  parts.push(formatRelative(file.uploadedAt))
  if (file.expiresOn && file.expiresInDays === null) {
    parts.push(`expires ${formatDateOnly(file.expiresOn)}`)
  }
  parts.push(formatBytes(file.byteSize))

  return (
    <p className="m-0 mt-1 truncate text-chip text-text-faint">
      {parts.map((part, index) => (
        <span key={index}>{index > 0 && ' · '}{part}</span>
      ))}
    </p>
  )
}

function FileMenuButton({ menu, name }: { menu: MenuItem[]; name: string }) {
  const [at, setAt] = useState<{ x: number; y: number } | null>(null)
  return (
    <>
      <button
        type="button"
        aria-label={`More for ${name}`}
        onClick={(event) => {
          const rect = event.currentTarget.getBoundingClientRect()
          setAt({ x: rect.right - 8, y: rect.bottom + 4 })
        }}
        className="-mr-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-field text-text-faint hover:bg-hover hover:text-text"
      >
        <IconMoreVertical size={16} />
      </button>
      {at && <ContextMenu items={menu} x={at.x} y={at.y} onClose={() => setAt(null)} />}
    </>
  )
}

/** The legend under the files, so the two visibility words are readable cold. */
function VisibilityKey() {
  return (
    <p className="m-0 text-[11px] leading-[1.45] text-text-faint">
      Visibility: <b className="font-semibold text-text-muted">Internal</b> office only ·{' '}
      <b className="font-semibold text-text-muted">Crew</b> opens on the crew iPad for visits here ·{' '}
      <b className="font-semibold text-text-muted">Customer portal</b> the customer can see it.
      Photos taken on visits and quotes roll up here automatically with their source.
    </p>
  )
}

function FilesSkeleton() {
  return (
    <div className="flex flex-col gap-2" aria-hidden>
      {[0, 1, 2].map((row) => (
        <div key={row} className="flex items-center gap-3 rounded-record border border-line bg-surface px-[18px] py-3">
          <span className="h-11 w-11 shrink-0 animate-pulse rounded-field bg-line-soft" />
          <span className="h-3 flex-1 animate-pulse rounded bg-line-soft" />
        </div>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Describing one
// ---------------------------------------------------------------------------

/**
 * Rename, retype, set who can see it, and say when it expires.
 *
 * Expiry is on every type rather than only insurance, because a permit, a
 * pesticide licence and a signed contract all lapse, and a field that appears
 * for one type is a field nobody finds for the others. Setting it arms the
 * nightly renewal task (§7.4) — the note under the field says so, because a
 * date that quietly creates work on somebody's list should say it will.
 */
/**
 * One dialog, one file or twenty.
 *
 * Everything it asks — what these are, who can see them, when they expire —
 * is the same answer for a batch somebody just dropped in one gesture, so it
 * is asked once and applied to all of them. The only per-file answer is the
 * name, and files arrive already named; so the name field appears for one
 * file and the batch gets a list of what it is about to change instead.
 */
export function DescribeFile({
  files, onClose, onSave,
}: {
  files: RecordFile[]
  onClose: () => void
  onSave: (changes: Record<string, unknown>) => Promise<void>
}) {
  const file = files[0]!
  const one = files.length === 1
  const [name, setName] = useState(file.name)
  const [caption, setCaption] = useState(one ? file.caption : '')
  const [type, setType] = useState<FileType>(file.type)
  const [visibility, setVisibility] = useState<FileVisibility>(file.visibility)
  const [expiresOn, setExpiresOn] = useState(one ? file.expiresOn ?? '' : '')
  const [busy, setBusy] = useState(false)

  async function submit() {
    setBusy(true)
    try {
      await onSave({
        // A batch keeps the names it arrived with.
        ...(one ? { originalName: name.trim() || file.name } : {}),
        caption: caption.trim(),
        type,
        visibility,
        expiresOn: expiresOn || null,
      })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={one ? 'Describe this file' : `Describe ${files.length} files`}
      footer={(
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={busy} onClick={() => void submit()}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </>
      )}
    >
      <div className="flex flex-col gap-3">
        {one
          ? (
            <Field label="Name">
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                className="h-8 w-full rounded-field border border-line bg-field px-2 text-body text-text"
              />
            </Field>
          )
          : (
            <Field label="Files">
              <ul className="m-0 flex max-h-[132px] list-none flex-col gap-0.5 overflow-y-auto p-0">
                {files.map((item) => (
                  <li key={item.id} className="truncate text-body text-text">
                    {item.name}
                    <span className="text-text-muted"> · {formatBytes(item.byteSize)}</span>
                  </li>
                ))}
              </ul>
            </Field>
          )}

        <Field label="What it is">
          <MenuSelect
            label="What it is"
            value={type}
            onChange={(next) => setType((next as FileType | null) ?? 'other')}
            options={FILE_TYPES.map((value) => ({ value, label: FILE_TYPE_LABELS[value] }))}
          />
        </Field>

        <Field label="Who can see it">
          <Segmented
            label="Who can see it"
            value={visibility}
            onChange={setVisibility}
            options={FILE_VISIBILITIES.map((value) => ({
              value, label: FILE_VISIBILITY_LABELS[value],
            }))}
          />
        </Field>

        <Field label="Expires">
          <div className="flex flex-col gap-1">
            <DateField label="Expires on" value={expiresOn} onChange={setExpiresOn} size="default" />
            <span className="text-[11px] text-text-faint">
              We will put a renewal task on this record 30 days before.
            </span>
          </div>
        </Field>

        <Field label="Note">
          <textarea
            value={caption}
            onChange={(event) => setCaption(event.target.value)}
            rows={2}
            placeholder="Optional — what somebody opening this should know."
            className="w-full resize-none rounded-field border border-line bg-field px-2 py-1.5 text-body text-text"
          />
        </Field>

        {one && file.source && (
          <p className="m-0 flex items-center gap-1.5 text-chip text-text-muted">
            <IconExternal size={13} />
            From {file.source.label}
          </p>
        )}
        {!one && (
          <p className="m-0 text-chip text-text-muted">
            These apply to all {files.length}. Change any of them afterwards from its ⋯ menu.
          </p>
        )}
      </div>
    </Modal>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="grid grid-cols-[110px_minmax(0,1fr)] items-start gap-3">
      <span className="pt-1.5 text-body text-text-muted">{label}</span>
      {children}
    </label>
  )
}

/** `1.2 MB`. Plan §3.8: thousands separators, and never more than one decimal. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  const kb = bytes / 1024
  if (kb < 1024) return `${Math.round(kb)} KB`
  const mb = kb / 1024
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`
}
